import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";

import React from "react";
import { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { GameCard } from "@pages/casino/components";

import { CasinoSliderContainer, TitleContainer } from "./elements.js";
import useController from "./useController.js";
import { getCardWidth } from "./utils.js";

const CasinoSlider = ({ isGamePage, data, type }) => {
	const {
		refs: { containerRef },
		helpers: { getSlidesPerGroup },
		state: { intersected, windowWidth, currentLanguage, userData },
	} = useController();

	const renderGameCard = (game, index, size = "1x1") => (
		<SwiperSlide
			key={index}
			style={{
				width: getCardWidth(size, windowWidth, window.TENANT),
			}}>
			<GameCard
				key={game?.id}
				size={size}
				game={game}
				type={type}
				isGamePage={isGamePage}
			/>
		</SwiperSlide>
	);

	const renderGridOptions = (grid) => {
		const items = [...(grid?.items || [])];

		return (
			<Swiper
				key={grid?.id}
				slidesPerView={"auto"}
				breakpoints={{ 1600: { slidesOffsetBefore: 50, slidesOffsetAfter: 50 } }}
				lazyPreloadPrevNext={1}
				slidesPerGroup={getSlidesPerGroup(windowWidth)}
				spaceBetween={10}
				modules={[Pagination, Navigation]}
				navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}>
				{items
					.sort((g1, g2) =>
						data?.type === "userLastPlayed" ? -1 : g1?.cardOrder - g2?.cardOrder
					)
					.map((item, index) => (
						<SwiperSlide
							key={index}
							style={{
								width: getCardWidth(
									item.options.cardSize,
									windowWidth,
									window.TENANT
								),
							}}>
							{item.options.cardType === "single" ? (
								<GameCard
									key={item.options.games[0]?.id}
									size={item.options.games[0]?.gameCardSize || "1x1"}
									game={data.gameList.find(
										(game) => game.id === item.options.games[0].id
									)}
									type={type}
									isGamePage={isGamePage}
								/>
							) : (
								<div
									style={{
										display: "flex",
										justifyContent: "flex-start",
										flexWrap: "wrap",
										gap: window.CASINO_GAME_CARD_V2 ? "5px 10px" : "10px",
									}}>
									{item.options.games.map((game) => (
										<GameCard
											key={game?.id}
											size={game.gameCardSize || "1x1"}
											game={data.gameList.find((data) => game.id === data.id)}
											type={type}
											isGamePage={isGamePage}
										/>
									))}
								</div>
							)}
						</SwiperSlide>
					))}
				<div className="swiper-button-prev"></div>
				<div className="swiper-button-next"></div>
			</Swiper>
		);
	};

	const renderSwiper = () => {
		const games = [...(data?.gameList || [])];

		return (
			<Swiper
				spaceBetween={10}
				slidesPerView={"auto"}
				lazyPreloadPrevNext={1}
				slidesPerGroup={getSlidesPerGroup(windowWidth)}
				breakpoints={{ 1600: { slidesOffsetBefore: 50, slidesOffsetAfter: 50 } }}
				modules={[Pagination, Navigation]}
				navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}>
				{games
					.sort((g1, g2) =>
						data?.type === "userLastPlayed" ? -1 : g1?.order - g2?.order
					)
					.map((game, index) =>
						renderGameCard(
							game,
							index,
							data?.configs?.gameGrids[index]?.bannerSize || "1x1"
						)
					)}
				<div className="swiper-button-prev"></div>
				<div className="swiper-button-next"></div>
			</Swiper>
		);
	};

	const topic = data?.topic?.[currentLanguage];

	if (!data?.isActive || !data?.gameList?.length || !topic) {
		return null;
	}

	const title = topic.includes("{user}") ? topic.replace("{user}", userData?.username) : topic;
	const isSwiper =
		data?.configs?.gameGrids?.length > 0 ||
		topic.includes("{user}") ||
		["aiNewGamesForYou", "userLastPlayed", "aiBecauseYouLiked"].includes(data?.type);

	return (
		<CasinoSliderContainer
			innerRef={containerRef}
			isBackground={topic.includes("{user}")}>
			{data?.gameList?.length > 0 && (
				<TitleContainer>
					{data?.icon_url && <img src={data?.icon_url} />}
					<span>{title}</span>
				</TitleContainer>
			)}

			{intersected
				? isSwiper
					? renderSwiper()
					: data?.configs?.gridOptions?.map(renderGridOptions)
				: null}
		</CasinoSliderContainer>
	);
};

export default CasinoSlider;
